import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import SplashScreen from './components/SplashScreen';
import ScrollToTop from './components/ScrollToTop';

// Public pages
import Layout from './components/Layout';
import Home from './pages/Home';
import About from './pages/About';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import SampleSolution from './pages/SampleSolution';
import Contact from './pages/Contact';

// Admin
import AdminLayout from './admin/AdminLayout';
import AdminLogin from './admin/pages/AdminLogin';
import AdminDashboard from './admin/pages/AdminDashboard';
import AdminProducts from './admin/pages/AdminProducts';
import AdminCategories from './admin/pages/AdminCategories';
import AdminSolutions from './admin/pages/AdminSolutions';
import AdminContacts from './admin/pages/AdminContacts';
import AdminAutoReplyMail from './admin/pages/AdminAutoReplyMail';
import AdminUsers from './admin/pages/AdminUsers';
import AdminSubmissions from './admin/pages/AdminSubmissions';
import AdminContent from './admin/pages/AdminContent';
import AdminSiteSettings from './admin/pages/AdminSiteSettings';
import AdminHomePage from './admin/pages/AdminHomePage';
import AdminAboutUs from './admin/pages/AdminAboutUs';
import AdminProfile from './admin/pages/AdminProfile';
import AdminRegister from './admin/pages/AdminRegister';
import ProtectedRoute from './admin/ProtectedRoute';

// User panel
import UserPanel from './pages/UserPanel';

// Injects Google Fonts + CSS variables based on admin typography settings
function FontInjector() {
  const { settings } = useSiteSettings();
  const t = settings.typography || {};

  useEffect(() => {
    const body    = t.bodyFont    || 'Inter';
    const heading = t.headingFont || 'Playfair Display';

    // Inject Google Fonts link
    const id = 'dynamic-fonts';
    let link = document.getElementById(id);
    if (!link) { link = document.createElement('link'); link.id = id; link.rel = 'stylesheet'; document.head.appendChild(link); }
    const families = [...new Set([body, heading])]
      .map(f => `family=${f.replace(/ /g, '+')}:ital,wght@0,400;0,500;0,600;0,700;1,400`)
      .join('&');
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;

    // Apply CSS variables to :root
    const root = document.documentElement;
    root.style.setProperty('--font-body',      `"${body}", sans-serif`);
    root.style.setProperty('--font-heading',   `"${heading}", serif`);
    root.style.setProperty('--color-base',     t.baseColor    || '#1A1A1A');
    root.style.setProperty('--color-accent',   t.accentColor  || '#B8A830');
    root.style.setProperty('--font-size-base', `${t.baseFontSize || 16}px`);
    root.style.setProperty('--font-style',     t.fontStyle    || 'normal');
  }, [t]);

  return null;
}

function AppRoutes() {
  const [ready, setReady] = useState(false);
  const handleDone = useCallback(() => setReady(true), []);

  return (
    <BrowserRouter>
      <FontInjector />
      {!ready && <SplashScreen onDone={handleDone} />}
      <ScrollToTop />
      <ToastContainer position="top-right" theme="dark" />
      <Routes>
        {/* Public */}
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/solutions" element={<SampleSolution />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/dashboard" element={<ProtectedRoute><UserPanel /></ProtectedRoute>} />
        </Route>

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/register" element={<AdminRegister />} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="profile" element={<AdminProfile />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="solutions" element={<AdminSolutions />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="auto-reply-mail" element={<AdminAutoReplyMail />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="submissions" element={<AdminSubmissions />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="site-settings" element={<AdminSiteSettings />} />
          <Route path="home-page" element={<AdminHomePage />} />
          <Route path="about-us" element={<AdminAboutUs />} />
        </Route>      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SiteSettingsProvider>
        <AppRoutes />
      </SiteSettingsProvider>
    </AuthProvider>
  );
}
